import React, { useState, useEffect, useCallback } from 'react'
import { SAFETY_MARGIN_OBJECT_NAME } from './SheetCanvas'
import PositionPopover from './PositionPopover'
import FlipPopover from './FlipPopover'
import RotatePopover from './RotatePopover'

function isValidSelection(obj) {
  if (!obj) return false
  if (obj.name === SAFETY_MARGIN_OBJECT_NAME) return false
  if (obj.type === 'activeSelection') return false
  if (obj.selectable === false || obj.evented === false) return false
  return true
}

export default function SelectionToolbar({
  canvas,
  onChange,
  onSelectionChange,
  onAddCopy,
  onRemoveCopy,
  sheetSize,
}) {
  const [selectedObject, setSelectedObject] = useState(null)

  useEffect(() => {
    if (!canvas) {
      onSelectionChange?.(null)
      return
    }

    const handleSelected = () => {
      const obj = canvas.getActiveObject()
      const nextSelectedObject = isValidSelection(obj) ? obj : null
      setSelectedObject(nextSelectedObject)
      onSelectionChange?.(nextSelectedObject)
    }

    const handleCleared = () => {
      setSelectedObject(null)
      onSelectionChange?.(null)
    }

    // Initialize from already-active object
    handleSelected()

    canvas.on('selection:created', handleSelected)
    canvas.on('selection:updated', handleSelected)
    canvas.on('selection:cleared', handleCleared)

    return () => {
      canvas.off('selection:created', handleSelected)
      canvas.off('selection:updated', handleSelected)
      canvas.off('selection:cleared', handleCleared)
    }
  }, [canvas, onSelectionChange])

  const handleAddCopy = useCallback(() => {
    if (!selectedObject) return
    onAddCopy?.(selectedObject.objectId)
  }, [onAddCopy, selectedObject])

  const handleRemove = useCallback(() => {
    if (!selectedObject) return
    onRemoveCopy?.(selectedObject.objectId)
  }, [onRemoveCopy, selectedObject])

  if (!selectedObject) return null

  return (
    <div className="selection-toolbar" data-testid="selection-toolbar">
      <span className="selection-toolbar__label">Selected:</span>

      <button className="selection-toolbar__btn" onClick={handleAddCopy} title="Add copy">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
        </svg>
        Add copy
      </button>

      <PositionPopover canvas={canvas} selectedObject={selectedObject} sheetSize={sheetSize} onChange={onChange} />
      <FlipPopover canvas={canvas} selectedObject={selectedObject} onChange={onChange} />
      <RotatePopover canvas={canvas} selectedObject={selectedObject} onChange={onChange} />

      <button className="selection-toolbar__btn selection-toolbar__btn--danger" onClick={handleRemove} title="Remove this copy">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
        </svg>
        Remove this copy
      </button>
    </div>
  )
}
